import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { getRunExecutionTrace, type RunExecutionTrace } from '@/services/aiStudioMonitorService';

interface RunExecutionTracePanelProps {
  runId: string;
}

export const RunExecutionTracePanel: React.FC<RunExecutionTracePanelProps> = ({ runId }) => {
  const { t } = useLanguage();
  const [trace, setTrace] = useState<RunExecutionTrace | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRunExecutionTrace(runId)
      .then((data) => {
        if (!cancelled) setTrace(data);
      })
      .catch(() => {
        if (!cancelled) setTrace(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (loading) {
    return <p className="text-base text-[var(--color-surface-text-secondary)]">{t('aiStudio.trace.loading')}</p>;
  }

  if (!trace) {
    return <p className="text-base text-[var(--color-error)]">{t('aiStudio.trace.loadFailed')}</p>;
  }

  return (
    <section className="mt-2 rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-3">
      <h3 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.trace.title')}
      </h3>
      <p className="mt-1 text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.trace.summary', {
          events: trace.summary.event_count,
          tokens: trace.summary.total_tokens,
          tools: trace.summary.tool_call_count,
        })}
      </p>
      <ol className="mt-2 max-h-48 space-y-1 overflow-y-auto text-base text-[var(--color-surface-text-secondary)]">
        {trace.timeline.map((event, index) => (
          <li key={`${event.type}-${event.at}-${index}`}>
            <span className="text-[var(--color-surface-text)]">{event.label}</span>
            {' · '}
            {new Date(event.at).toLocaleString()}
          </li>
        ))}
      </ol>
    </section>
  );
};
